import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { TRIP_STATUSES, type TripStatus } from '@kmdx/contracts';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';
import { t, TRIP_STATUS_BG } from '@/i18n';
import { formatKm, formatMinor, formatPlate, formatWhen } from '@/lib/format';
import { useTrips } from '@/api/hooks/trips';
import type { TripFilters } from '@/api/types';
import { StatusPill } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Field';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import { CardList, CardListEmpty, NarrowOnly, WideOnly } from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';

export function TripsPage() {
  const [status, setStatus] = useState<TripStatus | ''>('');
  const [term, setTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters: TripFilters = useMemo(() => {
    const value: TripFilters = { perPage: 50 };
    if (status) value.status = [status];
    if (term.trim()) value.q = term.trim();
    if (fromDate) value.fromDate = fromDate;
    return value;
  }, [status, term, fromDate]);

  const { data, isLoading, isError, refetch } = useTrips(filters);
  const items = data?.items ?? [];

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.trips.title}</h1>
        <span className="text-xs text-ink-dim">
          {data ? `${data.total} ${t.common.results}` : ''}
        </span>
      </div>

      <Card className="p-3">
        <div className="flex items-center gap-2">
          <Input
            className="min-w-0 flex-1"
            placeholder="Курс, бус, шофьор…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            aria-label={t.common.search}
          />
          {/* Търсенето остава на екрана, останалите филтри се прибират. */}
          <Button
            className="shrink-0 board:hidden"
            variant="secondary"
            icon={<SlidersHorizontal className="size-4" />}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((value) => !value)}
          >
            {t.common.filters}
          </Button>
        </div>
        <div
          className={cn(
            'mt-3 flex-wrap items-end gap-3 board:flex',
            filtersOpen ? 'flex' : 'hidden',
          )}
        >
          <Select
            className="w-full sm:w-44"
            aria-label={t.common.status}
            value={status}
            onChange={(e) => setStatus(e.target.value as TripStatus | '')}
            placeholder={`${t.common.status}: ${t.common.all}`}
            options={TRIP_STATUSES.map((s) => ({ value: s, label: TRIP_STATUS_BG[s] }))}
          />
          <Input
            type="date"
            className="w-full sm:w-36"
            aria-label={t.common.from}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : (
          <>
            {/* Тесен екран: курсът е карта — номер, бус, шофьор, километри, марж. */}
            <NarrowOnly>
              {items.length === 0 ? (
                <CardListEmpty>{t.trips.noTrips}</CardListEmpty>
              ) : (
                <CardList>
                  {items.map((trip) => {
                    const km = trip.actualDistanceKm ?? trip.plannedDistanceKm ?? 0;
                    const emptyShare = km > 0 && trip.emptyKm ? trip.emptyKm / km : 0;
                    return (
                      <li key={trip.id}>
                        <Link
                          to="/trips/$tripId"
                          params={{ tripId: trip.id }}
                          data-tap
                          className="block px-3 py-3"
                        >
                          <div className="flex items-baseline gap-2">
                            <span className="font-mono text-sm text-accent">{trip.number}</span>
                            <StatusPill status={trip.status} kind="trip" />
                            {trip.marginMinor !== null ? (
                              <span
                                className={cn(
                                  'ml-auto shrink-0 font-mono text-sm tabular',
                                  trip.marginMinor < 0 && 'text-danger',
                                )}
                              >
                                {formatMinor(trip.marginMinor, trip.ccy)}
                              </span>
                            ) : null}
                          </div>
                          {/* Празният ред „— — —" не казва нищо: редът се появява само с данни. */}
                          {trip.vehicleFleetNo || trip.driverName ? (
                            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm">
                              {trip.vehicleFleetNo ? <span>{trip.vehicleFleetNo}</span> : null}
                              {trip.vehiclePlate ? (
                                <span className="font-mono text-xs text-ink-faint">
                                  {formatPlate(trip.vehiclePlate)}
                                </span>
                              ) : null}
                              {trip.driverName ? (
                                <span className="text-ink-dim">{trip.driverName}</span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="mt-1 text-sm text-warn">без бус и шофьор</div>
                          )}
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-dim">
                            <span>
                              {t.trips.planned}: {formatWhen(trip.plannedStartAt)}
                            </span>
                            <span>{formatKm(km)}</span>
                            {trip.emptyKm !== null ? (
                              <span className={emptyShare > 0.3 ? 'text-warn' : undefined}>
                                {t.trips.emptyKm}: {formatKm(trip.emptyKm)}
                                {km > 0 ? ` (${Math.round(emptyShare * 100)} %)` : ''}
                              </span>
                            ) : null}
                          </div>
                          {(trip.orderNumbers ?? []).length > 0 ? (
                            <div className="mt-1 truncate font-mono text-xs text-ink-faint">
                              {(trip.orderNumbers ?? []).join(', ')}
                            </div>
                          ) : null}
                        </Link>
                      </li>
                    );
                  })}
                </CardList>
              )}
            </NarrowOnly>

            <WideOnly>
              <Table>
                <THead>
                  <TR>
                    <TH>{t.trips.number}</TH>
                    <TH>{t.common.status}</TH>
                    <TH>{t.trips.vehicle}</TH>
                    <TH>{t.trips.driver}</TH>
                    <TH>{t.trips.planned}</TH>
                    <TH>Заявки</TH>
                    <TH numeric>{t.trips.distance}</TH>
                    <TH numeric>{t.trips.emptyKm}</TH>
                    <TH numeric>{t.trips.margin}</TH>
                  </TR>
                </THead>
                <TBody>
                  {items.length === 0 ? (
                    <TableEmpty colSpan={9}>{t.trips.noTrips}</TableEmpty>
                  ) : (
                    items.map((trip) => {
                      const km = trip.actualDistanceKm ?? trip.plannedDistanceKm ?? 0;
                      const emptyShare = km > 0 && trip.emptyKm ? trip.emptyKm / km : 0;
                      return (
                        <TR key={trip.id} interactive>
                          <TD>
                            <Link
                              to="/trips/$tripId"
                              params={{ tripId: trip.id }}
                              className="font-mono text-sm text-accent hover:underline"
                            >
                              {trip.number}
                            </Link>
                          </TD>
                          <TD>
                            <StatusPill status={trip.status} kind="trip" />
                          </TD>
                          <TD className="whitespace-nowrap">
                            {trip.vehicleFleetNo ?? '—'}
                            <div className="font-mono text-[11px] text-ink-faint">
                              {formatPlate(trip.vehiclePlate)}
                            </div>
                          </TD>
                          <TD className="whitespace-nowrap">{trip.driverName ?? '—'}</TD>
                          <TD className="whitespace-nowrap">{formatWhen(trip.plannedStartAt)}</TD>
                          <TD className="max-w-[12rem] truncate font-mono text-xs">
                            {(trip.orderNumbers ?? []).join(', ') || '—'}
                          </TD>
                          <TD numeric>{formatKm(km)}</TD>
                          <TD numeric className={emptyShare > 0.3 ? 'text-warn' : undefined}>
                            {formatKm(trip.emptyKm)}
                            <div className="text-[11px] text-ink-faint">
                              {km > 0 ? `${Math.round(emptyShare * 100)} %` : ''}
                            </div>
                          </TD>
                          <TD
                            numeric
                            className={(trip.marginMinor ?? 0) < 0 ? 'text-danger' : undefined}
                          >
                            {formatMinor(trip.marginMinor, trip.ccy)}
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </WideOnly>
          </>
        )}
      </Card>
    </div>
  );
}
